import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useAuthStore, isAdminRole, isMasterRole } from '../store';
import { EmptyState, Loader, PageHeader, StatusBadge, formatDateTime } from '../components/Common';

export function HomePage() {
  const user = useAuthStore((s) => s.user);
  const isMaster = isMasterRole(user?.role);
  const isAdmin = isAdminRole(user?.role);

  const { data, isLoading } = useQuery({
    queryKey: ['my-bookings', 'home'],
    queryFn: () => api.bookings.my(false),
  });

  const upcoming = (data ?? [])
    .filter((b) => ['pending', 'confirmed', 'tentative'].includes(b.status))
    .slice(0, 3);

  return (
    <div className="space-y-5">
      <PageHeader
        title={`Привет, ${user?.first_name ?? 'друг'}!`}
        subtitle="Запишитесь на маникюр в пару тапов"
      />

      <div className="grid grid-cols-2 gap-3">
        <Link to="/services" className="card flex flex-col items-start gap-1 active:opacity-80">
          <span className="text-2xl">💅</span>
          <span className="text-sm font-semibold">Каталог</span>
          <span className="text-xs text-tg-hint">все услуги</span>
        </Link>
        <Link to="/book" className="card flex flex-col items-start gap-1 bg-tg-button text-tg-button-text active:opacity-80">
          <span className="text-2xl">➕</span>
          <span className="text-sm font-semibold">Записаться</span>
          <span className="text-xs opacity-80">за 4 шага</span>
        </Link>
        <Link to="/my-bookings" className="card flex flex-col items-start gap-1 active:opacity-80">
          <span className="text-2xl">📋</span>
          <span className="text-sm font-semibold">Мои записи</span>
          <span className="text-xs text-tg-hint">и история</span>
        </Link>
        <Link to="/waitlist" className="card flex flex-col items-start gap-1 active:opacity-80">
          <span className="text-2xl">⏳</span>
          <span className="text-sm font-semibold">Лист ожидания</span>
          <span className="text-xs text-tg-hint">подписки</span>
        </Link>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-tg-hint">Ближайшие записи</h2>
        {isLoading ? (
          <Loader />
        ) : upcoming.length === 0 ? (
          <EmptyState
            icon="📅"
            title="Нет запланированных визитов"
            hint="Запишитесь, и здесь появится напоминание"
          />
        ) : (
          <div className="space-y-2">
            {upcoming.map((b) => (
              <Link
                to={`/bookings/${b.id}`}
                key={b.id}
                className="card flex items-center justify-between active:opacity-80"
              >
                <div>
                  <p className="font-semibold">{b.service?.name ?? 'Услуга'}</p>
                  <p className="text-xs text-tg-hint">{formatDateTime(b.slot_start)}</p>
                  <p className="text-xs text-tg-hint">
                    Мастер: {b.master?.first_name ?? ''}
                  </p>
                </div>
                <StatusBadge status={b.status} />
              </Link>
            ))}
          </div>
        )}
      </section>

      {(isMaster || isAdmin) && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-tg-hint">Сервисные разделы</h2>
          <div className="grid grid-cols-2 gap-3">
            {isMaster && (
              <Link to="/master/calendar" className="card flex flex-col gap-1 active:opacity-80">
                <span className="text-xl">🗓️</span>
                <span className="text-sm font-semibold">Кабинет мастера</span>
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin" className="card flex flex-col gap-1 active:opacity-80">
                <span className="text-xl">🛠️</span>
                <span className="text-sm font-semibold">Админ-панель</span>
              </Link>
            )}
          </div>
        </section>
      )}

      {!isMaster && (
        <section>
          <Link to="/become-master" className="card flex items-center justify-between active:opacity-80">
            <div>
              <p className="font-semibold">Хотите принимать клиентов?</p>
              <p className="text-xs text-tg-hint">Подайте заявку на роль мастера</p>
            </div>
            <span>›</span>
          </Link>
        </section>
      )}
    </div>
  );
}
